import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/hr/holidays";

// Fetch Holidays
export const fetchHolidays = createAsyncThunk("holidays/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(API_URL);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch holidays");
  }
});

// Add Holiday
export const addHoliday = createAsyncThunk("holidays/add", async (holidayData, { rejectWithValue }) => {
  try {
    const response = await hrmsPostRequest(API_URL, holidayData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to add holiday");
  }
});

// Update Holiday
export const updateHoliday = createAsyncThunk("holidays/update", async ({ id, updatedData }, { rejectWithValue }) => {
  try {
    const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to update holiday");
  }
});

// Delete Holiday
export const deleteHoliday = createAsyncThunk("holidays/delete", async (id, { rejectWithValue }) => {
  try {
    await hrmsDeleteRequest(`${API_URL}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to delete holiday");
  }
});

// Fetch Holidays for a specific date
export const fetchHolidaysForDate = createAsyncThunk("holidays/fetchForDate", async (date, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(`${API_URL}/date/${date}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch holidays for date");
  }
});

// Fetch Holidays for a date range
export const fetchHolidaysForDateRange = createAsyncThunk("holidays/fetchForDateRange", async ({ startDate, endDate }, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(`${API_URL}/range?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch holidays for date range");
  }
});

const holidaySlice = createSlice({
  name: "holidays",
  initialState: {
    holidays: [],
    holidaysForDate: [],
    holidaysForDateRange: [],
    status: "idle",
    error: null,
    deleteStatus: "idle",
  },
  reducers: {
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHolidays.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.holidays = action.payload;
        state.error = null;
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(addHoliday.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addHoliday.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.holidays.push(action.payload);
        state.error = null;
      })
      .addCase(addHoliday.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(updateHoliday.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateHoliday.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.holidays.findIndex((h) => h.id === action.payload.id);
        if (index !== -1) {
          state.holidays[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateHoliday.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(deleteHoliday.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteHoliday.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.holidays = state.holidays.filter((h) => h.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteHoliday.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload;
      })
      .addCase(fetchHolidaysForDate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHolidaysForDate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.holidaysForDate = action.payload;
        state.error = null;
      })
      .addCase(fetchHolidaysForDate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchHolidaysForDateRange.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHolidaysForDateRange.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.holidaysForDateRange = action.payload;
        state.error = null;
      })
      .addCase(fetchHolidaysForDateRange.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { resetDeleteStatus: resetHolidayDeleteStatus } = holidaySlice.actions;
export default holidaySlice.reducer;